import useJobDispatcher from 'containers/HiringManager/Jobs.util';
import { CreateCandidateWorkFlow, CreateCandidateWorkflowSteps } from 'pages/B2B/Jobs/CreateCandidateWorkFlow';
import { CreateCandidateMeetingRequest } from 'containers/Candidate/ICandidate';
import React, { useEffect, useMemo, useState } from 'react';
import { Draggable, Droppable } from 'react-beautiful-dnd';
import { Button, Dropdown } from 'react-bootstrap';
import styled from 'styled-components';
import { Plus } from 'styled-icons/bootstrap';
import { SortNumericDown } from 'styled-icons/bootstrap';
import { Candidate } from 'types/jobPipeline';
import { STATUS_TYPE } from 'types/Jobs';
import JobCard from '../components/JobCard';
import { useJobPipeline } from '../contexts/JobPipelineContext';
import { candidateService } from 'services/candidate';
import { getJobCandidatesAction } from 'actions/hiringManager/jobs/jobsActions';
import { RootState } from "store";
import { useSelector } from "react-redux";
import { toast } from 'react-toastify';

export type SortOption = 'default' | 'name_asc' | 'name_desc' | 'applied_newest' | 'applied_oldest' | 'modified_newest' | 'modified_oldest' | 'ai_score' | 'job_match';

export const sortOptions: { value: SortOption; label: string }[] = [
  { value: 'default', label: 'Default' },
  { value: 'name_asc', label: 'Name (A-Z)' },
  { value: 'name_desc', label: 'Name (Z-A)' },
  { value: 'applied_newest', label: 'Applied (Newest)' },
  { value: 'applied_oldest', label: 'Applied (Oldest)' },
  { value: 'modified_newest', label: 'Modified (Newest)' },
  { value: 'modified_oldest', label: 'Modified (Oldest)' },
  { value: 'ai_score', label: 'AI Score (High to Low)' },
  { value: 'job_match', label: 'Job Match (High to Low)' },
];

const SectionContainer = styled.div<{ isDraggingOver: boolean; isDropDisabled: boolean }>`
  margin-bottom: 16px;
  padding: 24px;
  margin: 10px;
  background-color: ${(props) =>
    props.isDraggingOver ? (props.isDropDisabled ? '#f8d7da' : '#e0e6f8') : '#F0F3FA'};
  border-radius: 8px;
  width: 100%;
  box-sizing: border-box;
  transition: background-color 0.3s ease;
`;

const SectionHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 12px;
  margin-bottom: 16px;
`;

const SectionTitleWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  min-width: 0;
  flex: 1;
`;

const SectionTitle = styled.h5`
  margin: 0;
  font-size: 16px;
  font-weight: 600;
  color: #333333;
`;

const CandidateCount = styled.span`
  font-size: 14px;
  font-weight: 500;
  color: #757575;
`;

const SortDropdown = styled(Dropdown)<{ $isActive: boolean }>`
  flex-shrink: 0;

  .dropdown-toggle {
    display: flex;
    align-items: center;
    gap: 6px;
    outline: none !important;
    height: 32px;
    font-weight: 600;
    font-size: 12px;
    background: ${props => props.$isActive ? '#F2F7FD' : '#F4F4F4'};
    color: ${props => props.$isActive ? '#315CD5' : 'rgba(23, 23, 23, 0.6)'};
    border-radius: 20px;
    box-shadow: 0px 2px 8px rgba(0, 0, 0, 0.15);
    cursor: pointer;
    border: ${props => props.$isActive ? '1px solid #5B94E3' : '0'};
    padding: 6px 12px;
    transition: all 0.2s ease;

    &:hover, &:focus {
      background: ${props => props.$isActive ? '#E8F0FD' : '#E3E3E3'} !important;
      color: ${props => props.$isActive ? '#315CD5' : '#171717'} !important;
      border-color: ${props => props.$isActive ? '#5B94E3' : 'transparent'} !important;
      box-shadow: 0px 4px 12px rgba(0, 0, 0, 0.2) !important;
    }

    &:active {
      background: #E3E3E3 !important;
      box-shadow: 0px 2px 4px rgba(0, 0, 0, 0.15) !important;
    }

    &::after {
      margin-left: 2px;
      border-top-color: ${props => props.$isActive ? '#315CD5' : 'rgba(23, 23, 23, 0.6)'};
    }
  }

  .dropdown-menu {
    font-size: 12px;
    min-width: 180px;
    border-radius: 10px;
    box-shadow: 0px 4px 15px rgba(0, 0, 0, 0.15);
    border: none;
    padding: 8px 0;
    margin-top: 4px;
  }

  .dropdown-item {
    font-size: 12px;
    font-weight: 500;
    padding: 8px 16px;
    color: #171717;
    transition: all 0.15s ease;

    &:hover {
      background-color: #F2F7FD;
      color: #315CD5;
    }

    &:active, &.active {
      background-color: #315CD5;
      color: #FFFFFF;
    }
  }
`;

const SortIcon = styled(SortNumericDown)<{ $isActive: boolean }>`
  width: 14px;
  height: 14px;
  color: ${props => props.$isActive ? '#315CD5' : 'rgba(23, 23, 23, 0.6)'};
`;

const SortingNotice = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 6px 12px;
  background: #FFF8E6;
  border: 1px solid #FFE0A3;
  border-radius: 6px;
  font-size: 11px;
  color: #8B6914;
  margin-bottom: 12px;
`;

const ResetButton = styled.button`
  background: none;
  border: none;
  color: #315CD5;
  font-size: 11px;
  font-weight: 600;
  cursor: pointer;
  padding: 0;
  text-decoration: underline;
  &:hover {
    color: #1a3d8f;
  }
`;

const AddCandidateButton = styled(Button)`
  align-items: center;
  border: 1px solid #325cd6;
  font-weight: bold;
  font-size: 14px;
  border-radius: 8px;
  padding: 8px 16px;
  cursor: pointer;
  justify-content: center;
  width: 100%;
  margin-top: 16px;
`;

interface SectionProps {
  sectionId: string;
  title: string;
  candidates: Candidate[];
  onMenuAction: (action: string, id: string) => void;
}

const sortCandidates = (candidates: Candidate[], sortBy: SortOption): Candidate[] => {
  if (sortBy === 'default') return candidates;

  return [...candidates].sort((a, b) => {
    switch (sortBy) {
      case 'name_asc':
        return a.fullname.localeCompare(b.fullname);
      case 'name_desc':
        return b.fullname.localeCompare(a.fullname);
      case 'applied_newest':
        return (b.appliedAt || 0) - (a.appliedAt || 0);
      case 'applied_oldest':
        return (a.appliedAt || 0) - (b.appliedAt || 0);
      case 'modified_newest':
        return new Date(b.lastActivityAt || 0).getTime() - new Date(a.lastActivityAt || 0).getTime();
      case 'modified_oldest':
        return new Date(a.lastActivityAt || 0).getTime() - new Date(b.lastActivityAt || 0).getTime();
      case 'ai_score':
        return (b.candidateEvaluationDetails?.aiEvaluationScore || 0) - (a.candidateEvaluationDetails?.aiEvaluationScore || 0);
      case 'job_match':
        return (b.candidateResumeDetails?.jobMatchScore || 0) - (a.candidateResumeDetails?.jobMatchScore || 0);
      default:
        return 0;
    }
  });
};

const Section: React.FC<SectionProps> = ({ sectionId, title, candidates, onMenuAction }) => {
  const { currentJob, showFavoritesOnly, fetchJobById, updateFavoriteStatus, loadJobs, updateSectionSort } = useJobPipeline();
  const { updateCandidateInfoDispatcher } = useJobDispatcher();
  const expertId = useSelector((state: RootState) => state.auth.user.expertId);

  const [showCreateCandidate, setShowCreateCandidate] = useState<boolean>(false);
  const [startWorkFlow, setStartWorkFlow] = useState<CreateCandidateWorkflowSteps | undefined>();
  const [sortBy, setSortBy] = useState<SortOption>('default');

  // Sync sorting state with context so drag handler knows about it
  useEffect(() => {
    updateSectionSort(sectionId, sortBy);
  }, [sectionId, sortBy, updateSectionSort]);

  const sortedCandidates = useMemo(() => sortCandidates(candidates, sortBy), [candidates, sortBy]);
  const currentSortLabel = sortOptions.find(opt => opt.value === sortBy)?.label || 'Default';

  const updateCandidateResume = async (candidateState: STATUS_TYPE, candidateId: string, url: string) => {
    if (currentJob) {
      await updateCandidateInfoDispatcher(candidateState, candidateId, { resumeUrl: url });
      await fetchJobById(currentJob.jobId);
    }
  };

  const handleAddCandidate = () => {
    setStartWorkFlow(CreateCandidateWorkflowSteps.CREATE_CANDIDATE);
    setShowCreateCandidate(true);
  };

  const handleMeetingBooked = (payload: CreateCandidateMeetingRequest) => {
    if (currentJob?.jobId) {
        candidateService.createMeeting(payload)
            .then(() => {
                getJobCandidatesAction({ jobId: currentJob.jobId, expertId });
                if (currentJob) {
                  loadJobs(currentJob.jobId);
                }
                setShowCreateCandidate(false);
                toast.success("Meeting booked successfully!");
            })
            .catch((error: any) => {
                toast.error("Failed to book the meeting, please try again.");
                console.error(error);
                setShowCreateCandidate(false);
            });
    }
};

  const isSortingActive = sortBy !== 'default';

  return (
    <Droppable isDropDisabled={showFavoritesOnly || isSortingActive} droppableId={sectionId}>
      {(provided, snapshot) => (
        <SectionContainer
          ref={provided.innerRef}
          isDraggingOver={snapshot.isDraggingOver}
          isDropDisabled={isSortingActive}
          {...provided.droppableProps}
        >
          <SectionHeader>
            <SectionTitleWrapper>
              <SectionTitle>{title}</SectionTitle>
              <CandidateCount>{candidates.length}</CandidateCount>
            </SectionTitleWrapper>
            <SortDropdown $isActive={sortBy !== 'default'}>
              <Dropdown.Toggle variant="light" size="sm">
                <SortIcon $isActive={sortBy !== 'default'} />
                {currentSortLabel}
              </Dropdown.Toggle>
              <Dropdown.Menu>
                {sortOptions.map((option) => (
                  <Dropdown.Item
                    key={option.value}
                    active={sortBy === option.value}
                    onClick={() => setSortBy(option.value)}
                  >
                    {option.label}
                  </Dropdown.Item>
                ))}
              </Dropdown.Menu>
            </SortDropdown>
          </SectionHeader>
          {sectionId === 'talent_pool' && (
            <AddCandidateButton className="my-2" variant={"outline-primary"} onClick={handleAddCandidate}>
              <Plus size={24} /> Add a Candidate
            </AddCandidateButton>
          )}
          {isSortingActive && (
            <SortingNotice>
              <span>Drop disabled in this section while sorting is applied.</span>
              <ResetButton onClick={() => setSortBy('default')}>Reset</ResetButton>
            </SortingNotice>
          )}
          {sortedCandidates.map((candidate, index) => (
            <Draggable
              key={candidate.candidateId}
              draggableId={candidate.candidateId}
              index={index}
              isDragDisabled={showFavoritesOnly}
            >
              {(provided) => (
                <JobCard
                  ref={provided.innerRef}
                  candidate={candidate}
                  index={index}
                  onFavoriteToggle={(id: string, favorite: boolean) => updateFavoriteStatus(id, favorite, sectionId)}
                  onMenuAction={onMenuAction}
                  {...provided.draggableProps}
                  {...provided.dragHandleProps}
                />
              )}
            </Draggable>
          ))}
          {provided.placeholder}
          {showCreateCandidate && currentJob?.jobId && currentJob?.trackId && (
            <CreateCandidateWorkFlow
              setShowCreateCandidate={setShowCreateCandidate}
              jobId={currentJob.jobId}
              trackId={currentJob.trackId}
              startWorkFlow={startWorkFlow ?? CreateCandidateWorkflowSteps.SELECT_PRODUCT}
              handleMeetingBooked={handleMeetingBooked}
              updateCandidateResume={updateCandidateResume}
            />
          )}
        </SectionContainer>
      )}
    </Droppable>
  );
};

export default Section;